import type { PdfFormCoordinateOrigin, PdfFormRect } from "./types";

export interface FormPageSize {
  width: number;
  height: number;
}
export interface FormBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
export type FormResizeCorner = "nw" | "ne" | "sw" | "se";

export function isValidRect(rect: PdfFormRect): boolean {
  return rect.every(Number.isFinite) && rect[2] > rect[0] && rect[3] > rect[1];
}

/** Convert to top-left page coordinates before applying zoom. */
export function rectToBox(
  rect: PdfFormRect,
  page: FormPageSize,
  origin: PdfFormCoordinateOrigin
): FormBox {
  return {
    x: rect[0],
    y: origin === "bottom-left" ? page.height - rect[3] : rect[1],
    width: rect[2] - rect[0],
    height: rect[3] - rect[1],
  };
}

export function boxToRect(
  box: FormBox,
  page: FormPageSize,
  origin: PdfFormCoordinateOrigin
): PdfFormRect {
  return origin === "bottom-left"
    ? [
        box.x,
        page.height - box.y - box.height,
        box.x + box.width,
        page.height - box.y,
      ]
    : [box.x, box.y, box.x + box.width, box.y + box.height];
}

export function clampBox(box: FormBox, page: FormPageSize): FormBox {
  const width = Math.min(page.width, Math.max(4, box.width));
  const height = Math.min(page.height, Math.max(4, box.height));
  return {
    x: Math.max(0, Math.min(page.width - width, box.x)),
    y: Math.max(0, Math.min(page.height - height, box.y)),
    width,
    height,
  };
}

/** Keep the opposite corner fixed and stop a resize at page edges. */
export function resizeBox(
  box: FormBox,
  corner: FormResizeCorner,
  dx: number,
  dy: number,
  page: FormPageSize
): FormBox {
  const right = box.x + box.width;
  const bottom = box.y + box.height;
  const x = corner.includes("w")
    ? Math.min(right - 4, Math.max(0, box.x + dx))
    : box.x;
  const y = corner.includes("n")
    ? Math.min(bottom - 4, Math.max(0, box.y + dy))
    : box.y;
  const width = corner.includes("e")
    ? Math.max(4, Math.min(page.width - x, box.width + dx))
    : right - x;
  const height = corner.includes("s")
    ? Math.max(4, Math.min(page.height - y, box.height + dy))
    : bottom - y;
  return clampBox({ x, y, width, height }, page);
}
